import type {
  BlockWire,
  PlannedExerciseWire,
  RecordKind,
  ReferenceSessionWire,
  TargetUnit,
  TargetWire,
  WorkoutPlanWire,
} from "./types.js";
import { referenceDate, wireDate } from "./time.js";

export interface Target {
  amount: number;
  upper: number | null;
  unit: TargetUnit;
}

export interface Exercise {
  id: string;
  name: string;
  target: Target | null;
  note: string | null;
  perSide: boolean;
  isBodyweight: boolean;
  needsCheck: boolean;
  recordOverride: RecordKind | null;
  groups: number | null;
  /** Set only by `TrackSession.adapt` (§19.1), so the weights reading's wire is unchanged. */
  isResisted?: boolean;
  plannedLoad?: number | null;
  /** A rep of two or more runs (§19.1): each part's metres, the target their sum. */
  parts?: number[];
  /** The break after each part but the last, as he wrote it (`60s`), `""` where none (§19.1). */
  partBreaks?: string[];
}

export interface Block {
  id: string;
  sets: number;
  note: string | null;
  exercises: Exercise[];
  isPrep: boolean;
}

export interface Reference {
  id: string;
  label: string;
  lines: string[];
  isBeforeLifts: boolean;
}

export interface Plan {
  id: string;
  title: string;
  /** Epoch milliseconds. */
  date: number | null;
  createdAt: number;
  sourceText: string | null;
  interpretedBy: "rules" | "onDevice" | "cloud" | null;
  blocks: Block[];
  alsoToday: Reference[];
  teamSessionID: string | null;
  /** The coach's place for the day (DESIGN §9.4); absent or null when none. */
  location?: string | null;
}

export function newID(): string {
  const random = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto?.randomUUID;
  if (random !== undefined) return random.call((globalThis as { crypto?: object }).crypto).toUpperCase();
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16));
  hex[12] = "4";
  hex[16] = ((parseInt(hex[16]!, 16) & 0x3) | 0x8).toString(16);
  const s = hex.join("");
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`.toUpperCase();
}

export function exercise(fields: Partial<Exercise> & { name: string }): Exercise {
  return {
    id: newID(),
    target: null,
    note: null,
    perSide: false,
    isBodyweight: false,
    needsCheck: false,
    recordOverride: null,
    groups: null,
    ...fields,
  };
}

export function isWorking(block: Block): boolean {
  return !block.isPrep;
}

export function groupCount(exercise: Exercise): number | null {
  return exercise.groups !== null && exercise.groups > 1 ? exercise.groups : null;
}

/** `PlannedExercise.groupsPhrase`: what a phone that predates `groups` reads in the note. */
export function groupsPhrase(exercise: Exercise): string | null {
  const groups = groupCount(exercise);
  const target = exercise.target;
  if (groups === null || target === null) return null;
  const amount = target.upper !== null ? `${target.amount}-${target.upper}` : `${target.amount}`;
  const unit = target.unit === "reps" ? "" : target.unit === "seconds" ? "s" : "m";
  return `${groups}×${amount}${unit} each round`;
}

/**
 * `60s, 30s, 60s between`, or `30s between` when every break is the same: what a phone that predates
 * `partBreaks` reads in the note (§19.1).
 */
export function breaksPhrase(breaks: readonly string[] | undefined): string | null {
  const written = (breaks ?? []).filter((b) => b !== "");
  if (written.length === 0) return null;
  return `${(new Set(written).size === 1 ? [written[0]!] : written).join(", ")} between`;
}

/** The cue without the breaks phrase, for a phone that shows the breaks in the rep. */
export function withoutBreaksPhrase(note: string | null | undefined, breaks: readonly string[] | undefined): string | null {
  const phrase = breaksPhrase(breaks);
  if (!note) return note ?? null;
  if (phrase === null) return note;
  const left = note.split(" · ").filter((f) => f !== phrase).join(" · ");
  return left === "" ? null : left;
}

/** A rep in parts as he wrote it: `300 m + 60s + 100 m`. */
export function partsText(parts: readonly number[], breaks: readonly string[] | undefined): string {
  return parts.map((m, i) => (i > 0 && breaks?.[i - 1] ? `${breaks[i - 1]} + ` : "") + `${m} m`).join(" + ");
}

function targetWire(target: Target): TargetWire {
  const wire: TargetWire = { amount: target.amount } as TargetWire;
  if (target.upper !== null) wire.upper = target.upper;
  wire.unit = target.unit;
  return wire;
}

function exerciseWire(e: Exercise): PlannedExerciseWire {
  const wire = { id: e.id, name: e.name } as PlannedExerciseWire;
  if (e.target !== null) wire.target = targetWire(e.target);
  if (e.note !== null) wire.note = e.note;
  wire.perSide = e.perSide;
  wire.isBodyweight = e.isBodyweight;
  wire.needsCheck = e.needsCheck;
  if (e.recordOverride !== null) wire.recordOverride = e.recordOverride;
  if (e.groups !== null) wire.groups = e.groups;
  wire.isResisted = e.isResisted ?? false;
  if (e.plannedLoad !== undefined && e.plannedLoad !== null) wire.plannedLoad = e.plannedLoad;
  if (e.parts !== undefined) wire.parts = e.parts.slice();
  if (e.partBreaks !== undefined) wire.partBreaks = e.partBreaks.slice();
  return wire;
}

export function blockWire(b: Block): BlockWire {
  const wire = { id: b.id, sets: b.sets } as BlockWire;
  if (b.note !== null) wire.note = b.note;
  wire.exercises = b.exercises.map(exerciseWire);
  wire.isPrep = b.isPrep;
  return wire;
}

function referenceWire(r: Reference): ReferenceSessionWire {
  return { id: r.id, label: r.label, lines: r.lines.slice(), isBeforeLifts: r.isBeforeLifts };
}

/** In the order Swift's synthesized `Encodable` writes the keys, absent when nil. */
export function planWire(plan: Plan): WorkoutPlanWire {
  const wire = { id: plan.id, title: plan.title } as WorkoutPlanWire;
  if (plan.date !== null) wire.date = wireDate(plan.date);
  wire.createdAt = wireDate(plan.createdAt);
  if (plan.sourceText !== null) wire.sourceText = plan.sourceText;
  if (plan.interpretedBy !== null) wire.interpretedBy = plan.interpretedBy;
  wire.blocks = plan.blocks.map(blockWire);
  wire.alsoToday = plan.alsoToday.map(referenceWire);
  if (plan.teamSessionID !== null) wire.teamSessionID = plan.teamSessionID;
  if (plan.location != null && plan.location !== "") wire.location = plan.location;
  return wire;
}

export function planFromWire(wire: WorkoutPlanWire): Plan {
  return {
    id: wire.id,
    title: wire.title,
    date: wire.date === undefined ? null : referenceDate + wire.date * 1000,
    createdAt: referenceDate + wire.createdAt * 1000,
    sourceText: wire.sourceText ?? null,
    interpretedBy: wire.interpretedBy ?? null,
    blocks: wire.blocks.map((b) => ({
      id: b.id,
      sets: b.sets,
      note: b.note ?? null,
      isPrep: b.isPrep ?? false,
      exercises: b.exercises.map((e) => ({
        id: e.id,
        name: e.name,
        target: e.target ? { amount: e.target.amount, upper: e.target.upper ?? null, unit: e.target.unit } : null,
        note: e.note ?? null,
        perSide: e.perSide ?? false,
        isBodyweight: e.isBodyweight ?? false,
        needsCheck: e.needsCheck ?? false,
        recordOverride: e.recordOverride ?? null,
        groups: e.groups ?? null,
      })),
    })),
    alsoToday: (wire.alsoToday ?? []).map((r) => ({
      id: r.id,
      label: r.label,
      lines: r.lines ?? [],
      isBeforeLifts: r.isBeforeLifts ?? false,
    })),
    teamSessionID: wire.teamSessionID ?? null,
    location: wire.location ?? null,
  };
}
